/**
 * SuccessPage - Order confirmation after successful payment
 * Route: /order/:sessionId/success
 */

import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { useBrandTheme } from "@/lib/brand-theme";
import { formatCurrency, formatDate } from "@/lib/utils";
import { analytics, trackPurchaseCompleted } from "@/lib/analytics";
import { getPostHogClient } from "@/lib/posthog";
import { Card } from "@/components/Card";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { ErrorMessage } from "@/components/ErrorMessage";
import { useOrder, useSession } from "@/api/hooks";
import { useTheme } from "@/lib/theme/ThemeProvider";
import { fetchRetailerConfig } from "@/lib/supabase-api";

// Secure checkout badge with theme toggle
function SecureBadge() {
  const { resolvedTheme, setTheme } = useTheme();

  const toggleTheme = () => {
    setTheme(resolvedTheme === "dark" ? "light" : "dark");
  };

  return (
    <div className="flex items-center gap-3">
      <div className="secure-badge">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
        <span>Order Complete</span>
      </div>
      <button
        onClick={toggleTheme}
        className="p-2 rounded-lg hover:bg-[var(--color-surface-secondary)] transition-colors"
        aria-label={`Switch to ${resolvedTheme === "dark" ? "light" : "dark"} mode`}
        title={`Switch to ${resolvedTheme === "dark" ? "light" : "dark"} mode`}
      >
        {resolvedTheme === "dark" ? (
          <svg
            className="w-5 h-5 text-[var(--color-text-secondary)]"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
        ) : (
          <svg
            className="w-5 h-5 text-[var(--color-text-secondary)]"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
            />
          </svg>
        )}
      </button>
    </div>
  );
}

export function SuccessPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("order_id");
  const paymentReference = searchParams.get("reference");
  const { resolvedTheme } = useTheme();
  const [enforceShipping, setEnforceShipping] = useState(false)

  const [selectedShippingMethod, setSelectedShippingMethod] = useState<{
    id: string;
    name: string;
    description: string;
    price_cents: number;
  } | null>(null);
  const { theme } = useBrandTheme();

  const hasTrackedPurchase = useRef(false);
  const hasTrackedGTMPurchase = useRef(false);
  const hasTrackedPostHogOrderCompleted = useRef(false);

  const { data: orderData, isLoading, error } = useOrder(orderId);
  const { data: sessionData } = useSession(sessionId || null);

  // Load shipping method from localStorage
  useEffect(() => {
    const savedShippingMethod = localStorage.getItem("selectedShippingMethod");
    if (savedShippingMethod) {
      try {
        const shippingOption = JSON.parse(savedShippingMethod);
        setSelectedShippingMethod(shippingOption);
      } catch (error) {
        console.error("Failed to parse shipping method from localStorage:", error);
        if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
          Sentry.captureException(error, {
            tags: { component: "SuccessPage", action: "parseShippingMethod" },
          });
        }
      }
    }
  }, []);

  useEffect(() => {
    const loadRetailerConfig = async () => {
      if (!theme?.id) return;
      try {
        const retailer = await fetchRetailerConfig(theme.id);
 
        setEnforceShipping(retailer?.config?.enforce_shipping || false)

      } catch (error) {
        console.error("Failed to fetch retailer config", error);
      }
    };
    loadRetailerConfig();
  }, [theme?.id]);

  // Track page view and purchase completion
  useEffect(() => {
    if (sessionId) {
      analytics.pageView("success");
    }
  }, [sessionId]);

  // Track purchase completed when order loads (only once per order)
  useEffect(() => {
    if (orderData && !hasTrackedPurchase.current) {
      hasTrackedPurchase.current = true;
      trackPurchaseCompleted(orderId || "", orderData.order.total_cents, orderData.items.length);
    }
  }, [orderData, orderId]);

  // Track PostHog order_completed event when order loads (only once per order)
  useEffect(() => {
    if (
      orderData &&
      sessionId &&
      sessionData?.session?.retailer_id &&
      !hasTrackedPostHogOrderCompleted.current
    ) {
      hasTrackedPostHogOrderCompleted.current = true;
      const posthog = getPostHogClient();
      posthog.capture("order_completed", {
        retailer_id: sessionData.session.retailer_id,
        session_id: sessionId,
        order_id: orderData.order.id,
        total: orderData.order.total_cents / 100,
        creator_id: orderData.order.customer_id || null,
      });
    }
  }, [orderData, sessionId, sessionData]);

  // Track GTM purchase event when order loads (only once per order)
  useEffect(() => {
    if (orderData && sessionData && !hasTrackedGTMPurchase.current) {
      hasTrackedGTMPurchase.current = true;
      try {
        (window as any).dataLayer?.push({
          event: "purchase",
          transaction_id: orderData.order.id,
          value: orderData.order.total_cents / 100,
          currency: "ZAR",
          link_id: sessionData.session?.context?.attribution?.linkId,
          utm_source: sessionData.session?.context?.attribution?.utmSource,
          utm_medium: sessionData.session?.context?.attribution?.utmMedium,
          utm_campaign: sessionData.session?.context?.attribution?.utmCampaign,
        });
      } catch (error) {
        console.error("GTM purchase error:", error);
        if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
          Sentry.captureException(error, {
            tags: { component: "SuccessPage", action: "gtmPurchase" },
          });
        }
      }
    }
  }, [orderData, sessionData]);

  // Calculate shipping cents
  const shippingCents = orderData?.order.shipping_cents || selectedShippingMethod?.price_cents || 0;

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[var(--color-bg-primary)] flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Error state
  if (error || !orderData) {
    return (
      <div className="min-h-screen bg-[var(--color-bg-primary)] flex items-center justify-center p-4">
        <ErrorMessage
          title="Failed to load order"
          message={error instanceof Error ? error.message : "Order not found"}
          className="max-w-md"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg-primary)] flex flex-col justify-between gap-5">
      {/* Header */}
      <div>
        <header className="bg-[var(--color-surface-primary)] border-b border-[var(--color-surface-border-light)]">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              {theme?.logo_url ? (
                <img src={theme.logo_url} alt={theme.name} className="h-8 w-auto" />
              ) : (
                <div className="text-[18px] font-semibold text-[var(--color-text-primary)]">
                  {theme?.name || "Checkout"}
                </div>
              )}
              <SecureBadge />
            </div>
          </div>
        </header>

        <main className="max-w-6xl mx-auto px-4 py-8">
          <div className="lg:grid lg:grid-cols-[1fr_380px] lg:gap-8">
            {/* Main Content */}
            <div>
              {/* Success Message */}
              <div className="text-center mb-8">
                <div className="mx-auto w-16 h-16 bg-[var(--color-success-light)] rounded-full flex items-center justify-center mb-4">
                  <svg
                    className="w-8 h-8 text-[var(--color-success)]"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>

                <h1 className="text-[24px] font-semibold text-[var(--color-text-primary)] mb-2">
                  Thank you for your order!
                </h1>
                <p className="text-[14px] text-[var(--color-text-secondary)]">
                  We've received your payment and your order is being processed.
                </p>
              </div>

              {/* Order Details */}
              {orderData && (
                <>
                  <Card className="mb-6">
                    <div className="flex items-center justify-between mb-5">
                      <h2 className="text-[16px] font-semibold text-[var(--color-text-primary)]">
                        Order Details
                      </h2>
                      <span className="px-2.5 py-1 text-[12px] font-medium bg-[var(--color-success-light)] text-[var(--color-success)] rounded-full">
                        {orderData.order.status === "paid" ? "Paid" : orderData.order.status}
                      </span>
                    </div>

                    <div className="space-y-3 text-[14px]">
                      <div className="flex justify-between">
                        <span className="text-[var(--color-text-secondary)]">Order Number</span>
                        <span className="font-medium text-[var(--color-text-primary)] font-mono text-[13px]">
                          {orderData.order.id.slice(0, 8).toUpperCase()}
                        </span>
                      </div>

                      <div className="flex justify-between">
                        <span className="text-[var(--color-text-secondary)]">Order Date</span>
                        <span className="font-medium text-[var(--color-text-primary)]">
                          {formatDate(orderData.order.created_at)}
                        </span>
                      </div>

                      {(paymentReference || orderData.order.payment_reference) && (
                        <div className="flex justify-between">
                          <span className="text-[var(--color-text-secondary)]">
                            Payment Reference
                          </span>
                          <span className="font-medium text-[var(--color-text-primary)] font-mono text-[13px]">
                            {paymentReference || orderData.order.payment_reference}
                          </span>
                        </div>
                      )}

                      <div className="flex justify-between">
                        <span className="text-[var(--color-text-secondary)]">Payment Status</span>
                        <span className="font-medium text-[var(--color-success)]">Confirmed</span>
                      </div>
                    </div>
                  </Card>

                  {/* Shipping Information */}
                  {orderData.order.shipping && enforceShipping && (
                    <Card className="mb-6">
                      <h2 className="text-[16px] font-semibold text-[var(--color-text-primary)] mb-4">
                        Shipping Address
                      </h2>

                      <div className="text-[14px] text-[#545454] space-y-3">
                        <p className="text-[var(--color-text-secondary)]">
                          {orderData.order.shipping.name}
                        </p>
                        <p className="text-[var(--color-text-secondary)]">
                          {orderData.order.shipping.address}
                        </p>
                        <p className="text-[var(--color-text-secondary)]">
                          {orderData.order.shipping.city}, {orderData.order.shipping.province}{" "}
                          {orderData.order.shipping.postal_code}
                        </p>
                        <p className="text-[var(--color-text-secondary)]">
                          {orderData.order.shipping.phone}
                        </p>
                        {orderData.order.shipping.email && (
                          <p className="text-[var(--color-text-secondary)]">
                            {orderData.order.shipping.email}
                          </p>
                        )}
                      </div>
                    </Card>
                  )}

                  {/* Next Steps */}
                  <Card className="bg-[var(--color-info-light)] border-[var(--color-info)]">
                    <div className="flex items-start gap-3">
                      <svg
                        className="w-5 h-5 text-[var(--color-info)] flex-shrink-0 mt-0.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <div className="text-[14px] text-[var(--color-text-primary)]">
                        <p className="font-medium mb-2">What happens next?</p>
                        <ul className="space-y-1.5 text-[var(--color-text-secondary)]">
                          {orderData?.order?.shipping?.email && (
                            <li className="flex items-start gap-2">
                              <span className="text-[var(--color-info)] mt-1">•</span>
                              <span>
                                You will receive a copy of the receipt via email shortly at{" "}
                                <span className="font-medium text-[var(--color-success)]">
                                  {orderData.order.shipping.email}
                                </span>
                              </span>
                            </li>
                          )}
                          {orderData?.order?.shipping?.phone && orderData?.order?.shipping?.phone !== 'Unknown' && (
                            <li className="flex items-start gap-2">
                              <span className="text-[var(--color-info)] mt-1">•</span>
                              <span>
                                You will receive a confirmation SMS/WhatsApp shortly at{" "}
                                <span className="font-medium text-[var(--color-success)]">
                                  {orderData.order.shipping.phone}
                                </span>
                              </span>
                            </li>
                          )}
                          <li className="flex items-start gap-2">
                            <span className="text-[var(--color-info)] mt-1">•</span>
                            <span>Your order will be processed and dispatched</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-[var(--color-info)] mt-1">•</span>
                            <span>You'll receive tracking information once shipped</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </Card>
                </>
              )}
            </div>

            {/* Order Summary Sidebar */}
            <div className="hidden lg:block">
              <div className="sticky top-8">
                {orderData && (
                  <Card>
                    <h2 className="text-[16px] font-semibold text-[var(--color-text-primary)] pb-4 border-b border-[var(--color-surface-border-light)]">
                      Order Summary
                    </h2>

                    {/* Items */}
                    <div className="divide-y divide-[var(--color-surface-border-light)]">
                      {orderData.items.map((item) => (
                        <div key={item.id} className="flex items-center gap-4 py-4">
                          <div className="relative">
                            <div className="w-12 h-12 bg-[var(--color-bg-secondary)] rounded border border-[var(--color-surface-border-light)] flex items-center justify-center overflow-hidden">
                              {item.image_url ? (
                                <img
                                  src={item.image_url}
                                  alt={item.title}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <svg
                                  className="w-6 h-6 text-[var(--color-text-placeholder)]"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1.5}
                                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                  />
                                </svg>
                              )}
                            </div>
                            <span className="absolute -top-1 -right-1 w-5 h-5 bg-[var(--color-text-muted)] text-white text-[10px] font-medium rounded-full flex items-center justify-center">
                              {item.quantity}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[14px] text-[var(--color-text-primary)] truncate">
                              {item.title}
                            </p>
                            <p className="text-[12px] text-[var(--color-text-muted)]">
                              SKU: {item.sku}
                            </p>
                          </div>
                          <div className="text-[14px] text-[var(--color-text-primary)] font-medium tabular-nums">
                            {formatCurrency(item.line_total_cents)}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Totals */}
                    <div className="border-t border-[var(--color-surface-border-light)] pt-4 space-y-3">
                      <div className="flex justify-between text-[14px]">
                        <span className="text-[var(--color-text-secondary)]">Subtotal</span>
                        <span className="text-[var(--color-text-primary)] tabular-nums">
                          {formatCurrency(
                            orderData.items.reduce((sum, item) => sum + item.line_total_cents, 0)
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[var(--color-text-secondary)]">Shipping</span>
                        <span className="text-[var(--color-text-primary)] font-medium tabular-nums">
                          {shippingCents > 0 ? formatCurrency(shippingCents) : "—"}
                        </span>
                      </div>
                      {selectedShippingMethod && shippingCents > 0 && (
                        <p className="text-[12px] text-[var(--color-text-muted)] -mt-1">
                          {selectedShippingMethod.name}
                        </p>
                      )}

                      <div className="flex justify-between pt-3 border-t border-[var(--color-surface-border-light)]">
                        <span className="text-[16px] font-semibold text-[var(--color-text-primary)]">
                          Total Paid
                        </span>
                        <span className="text-[18px] font-semibold text-[var(--color-text-primary)] tabular-nums">
                          {formatCurrency(orderData.order.total_cents + shippingCents)}
                        </span>
                      </div>
                    </div>
                  </Card>
                )}

                {/* Support */}
                <div className="mt-6 text-center text-[13px] text-[var(--color-text-muted)]">
                  <p>
                    Questions about your order?{" "}
                    <a
                      href="#"
                      className="text-[var(--color-secondary,var(--color-primary))] hover:text-[var(--color-secondary-hover,var(--color-primary-hover))] font-medium"
                    >
                      Contact Support
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Order Summary */}
          {orderData && (
            <div className="lg:hidden mt-8">
              <Card>
                <h2 className="text-[16px] font-semibold text-[var(--color-text-primary)] pb-4 border-b border-[var(--color-surface-border-light)]">
                  Order Summary
                </h2>

                <div className="divide-y divide-[var(--color-surface-border-light)]">
                  {orderData.items.map((item) => (
                    <div key={item.id} className="py-3 flex justify-between">
                      <div className="flex items-center gap-4">
                        <div className="relative">
                          <div className="w-12 h-12 bg-[var(--color-bg-secondary)] rounded border border-[var(--color-surface-border-light)] flex items-center justify-center overflow-hidden">
                            {item.image_url ? (
                              <img
                                src={item.image_url}
                                alt={item.title}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <svg
                                className="w-6 h-6 text-[var(--color-text-placeholder)]"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={1.5}
                                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                />
                              </svg>
                            )}
                          </div>
                          <span className="absolute -top-1 -right-1 w-5 h-5 bg-[var(--color-text-muted)] text-white text-[10px] font-medium rounded-full flex items-center justify-center">
                            {item.quantity}
                          </span>
                        </div>

                        <div>
                          <p className="text-[14px] font-medium text-[var(--color-text-primary)]">
                            {item.title}
                          </p>
                          <p className="text-[12px] text-[var(--color-text-muted)]">
                            SKU: {item.sku} × {item.quantity}
                          </p>
                        </div>
                      </div>

                      <span className="text-[14px] font-medium text-[var(--color-text-primary)] tabular-nums">
                        {formatCurrency(item.line_total_cents)}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="border-t border-[var(--color-surface-border-light)] pt-4 mt-4 space-y-2">
                  <div className="flex justify-between text-[14px]">
                    <span className="text-[var(--color-text-secondary)]">Subtotal</span>
                    <span className="text-[var(--color-text-primary)] tabular-nums">
                      {formatCurrency(
                        orderData.items.reduce((sum, item) => sum + item.line_total_cents, 0)
                      )}
                    </span>
                  </div>
                  {selectedShippingMethod && shippingCents > 0 && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-[var(--color-text-secondary)]">Shipping</span>
                        <span className="tabular-nums">{formatCurrency(shippingCents)}</span>
                      </div>
                      <p className="text-[12px] text-[var(--color-text-muted)] -mt-1">
                        {selectedShippingMethod.name}
                      </p>
                    </>
                  )}

                  <div className="flex justify-between pt-2 border-t border-[var(--color-surface-border-light)]">
                    <span className="font-semibold text-[var(--color-text-primary)]">
                      Total Paid
                    </span>
                    <span className="font-semibold text-[var(--color-text-primary)] tabular-nums">
                      {formatCurrency(orderData.order.total_cents + shippingCents)}
                    </span>
                  </div>
                </div>
              </Card>

              {/* Mobile Support */}
              <div className="mt-6 text-center text-[13px] text-[var(--color-text-muted)]">
                <p>
                  Questions about your order?{" "}
                  <a
                    href="#"
                    className="text-[var(--color-secondary,var(--color-primary))] hover:text-[var(--color-secondary-hover,var(--color-primary-hover))] font-medium"
                  >
                    Contact Support
                  </a>
                </p>
              </div>
            </div>
          )}
        </main>
      </div>

      <footer className="flex items-center border-t border-[var(--color-surface-border-light)] p-4 z-50 items-center justify-center gap-1.5 text-xs text-[var(--color-text-muted)]">
        <span>Powered by</span>
        {resolvedTheme === "light" ? (
          <img src="/images/nile-wordmark-dark.png" alt="Nile" className="h-3.5" />
        ) : (
          <img src="/images/nile-wordmark-light.png" alt="Nile" className="h-3.5" />
        )}
      </footer>
    </div>
  );
}
